/**
 * Image セクション。6 variants:
 *   full         — フル幅 1 枚
 *   gallery      — 3-4 枚グリッド
 *   carousel     — 1 枚表示 + ドットナビ (印刷は選択 1 枚のみ)
 *   inline_card  — 角丸+影付きカード
 *   before_after — 2 枚並置 (ビフォー/アフター ラベル付き)
 *   side_by_side — 画像 + 見出し + 本文の左右 50/50 (プロフィール・人物紹介向け)
 */
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  ArrayField,
  ImageField,
  SelectField,
  TextField,
  TextareaField,
} from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface FullConfig {
  src: string;
  caption: string;
}
interface GalleryItem {
  src: string;
  caption: string;
}
interface GalleryConfig {
  items: GalleryItem[];
}
interface CarouselItem {
  src: string;
  caption: string;
}
interface CarouselConfig {
  items: CarouselItem[];
  /** PDF 印刷時に出力する画像の index (0-based) */
  printIndex: number;
}
interface InlineCardConfig {
  src: string;
  caption: string;
}
interface BeforeAfterConfig {
  beforeSrc: string;
  beforeLabel: string;
  afterSrc: string;
  afterLabel: string;
  caption: string;
}
type SideBySideSide = 'left' | 'right';
interface SideBySideConfig {
  src: string;
  imageSide: SideBySideSide;
  eyebrow: string;
  title: string;
  body: string;
}

function FullEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<FullConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <ImageField
        label="画像"
        value={(config.src as string) ?? ''}
        onChange={(v) => {
          set({ src: v });
          onCommit();
        }}
      />
      <TextField
        label="キャプション"
        value={(config.caption as string) ?? ''}
        onChange={(v) => set({ caption: v })}
        onCommit={onCommit}
      />
    </div>
  );
}

function FullImage({ config }: RendererProps) {
  const v = config as unknown as FullConfig;
  return (
    <section>
      {v.src ? (
        <img src={v.src} alt={v.caption ?? ''} className="w-full rounded" />
      ) : (
        <div className="w-full aspect-video bg-[var(--bg-hover)] rounded flex items-center justify-center text-xs text-[var(--text-muted)]">
          画像未設定
        </div>
      )}
      {v.caption && (
        <p className="text-xs text-center text-[var(--text-muted)] mt-2">
          {v.caption}
        </p>
      )}
    </section>
  );
}

function GalleryEditor({ config, onChange }: EditorProps) {
  const items = ((config.items as GalleryItem[]) ?? []) as GalleryItem[];
  return (
    <ArrayField<GalleryItem>
      label="画像リスト"
      items={items}
      onChange={(next) => onChange({ ...config, items: next })}
      addLabel="画像を追加"
      emptyItem={() => ({ src: '', caption: '' })}
      renderItem={(item, _i, update) => (
        <div className="space-y-2">
          <ImageField
            label="画像"
            value={item.src}
            onChange={(v) => update({ src: v })}
          />
          <TextField
            label="キャプション"
            value={item.caption ?? ''}
            onChange={(v) => update({ caption: v })}
            onCommit={() => {}}
          />
        </div>
      )}
    />
  );
}

function GalleryImage({ config }: RendererProps) {
  const items = ((config as unknown as GalleryConfig).items ?? []) as GalleryItem[];
  if (items.length === 0) {
    return (
      <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="aspect-square bg-[var(--bg-hover)] rounded flex items-center justify-center text-[10px] text-[var(--text-muted)]"
          >
            画像 {i + 1}
          </div>
        ))}
      </section>
    );
  }
  return (
    <section className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {items.map((it, i) => (
        <figure key={i} className="space-y-1">
          {it.src ? (
            <img
              src={it.src}
              alt={it.caption ?? ''}
              className="w-full aspect-square object-cover rounded"
            />
          ) : (
            <div className="aspect-square bg-[var(--bg-hover)] rounded" />
          )}
          {it.caption && (
            <figcaption className="text-[11px] text-center text-[var(--text-muted)]">
              {it.caption}
            </figcaption>
          )}
        </figure>
      ))}
    </section>
  );
}

function CarouselEditor({ config, onChange, onCommit }: EditorProps) {
  const items = ((config.items as CarouselItem[]) ?? []) as CarouselItem[];
  const rawPrintIndex = (config.printIndex as number | undefined) ?? 0;
  const printIndex = items.length === 0
    ? 0
    : Math.min(Math.max(rawPrintIndex, 0), items.length - 1);
  return (
    <div className="space-y-3">
      <ArrayField<CarouselItem>
        label="画像リスト"
        items={items}
        onChange={(next) => {
          const nextPrint = next.length === 0
            ? 0
            : Math.min(printIndex, next.length - 1);
          onChange({ ...config, items: next, printIndex: nextPrint });
        }}
        addLabel="画像を追加"
        emptyItem={() => ({ src: '', caption: '' })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <ImageField
              label="画像"
              value={item.src}
              onChange={(v) => update({ src: v })}
            />
            <TextField
              label="キャプション"
              value={item.caption ?? ''}
              onChange={(v) => update({ caption: v })}
              onCommit={() => {}}
            />
          </div>
        )}
      />
      {items.length > 0 && (
        <SelectField<string>
          label="PDF 印刷時に出力する画像"
          value={String(printIndex)}
          onChange={(v) => onChange({ ...config, printIndex: Number(v) })}
          onCommit={onCommit}
          options={items.map((it, i) => ({
            value: String(i),
            label: `#${i + 1}${it.caption ? ` - ${it.caption}` : ''}`,
          }))}
        />
      )}
    </div>
  );
}

function CarouselImage({ config }: RendererProps) {
  const items = ((config as unknown as CarouselConfig).items ?? []) as CarouselItem[];
  const rawPrintIndex = (config.printIndex as number | undefined) ?? 0;
  const printIndex = items.length === 0
    ? 0
    : Math.min(Math.max(rawPrintIndex, 0), items.length - 1);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= items.length) setActiveIndex(0);
  }, [items.length, activeIndex]);

  if (items.length === 0) {
    return (
      <section>
        <div className="w-full aspect-video bg-[var(--bg-hover)] rounded flex items-center justify-center text-xs text-[var(--text-muted)]">
          画像未設定
        </div>
      </section>
    );
  }

  const go = (dir: -1 | 1) => {
    setActiveIndex((i) => (i + dir + items.length) % items.length);
  };

  return (
    <section className="doc-carousel">
      <div className="relative">
        {items.map((it, i) => (
          <div
            key={i}
            className="doc-carousel-slide"
            data-active={i === activeIndex ? 'true' : 'false'}
            data-print-active={i === printIndex ? 'true' : 'false'}
          >
            {it.src ? (
              <img
                src={it.src}
                alt={it.caption ?? ''}
                className="w-full rounded"
              />
            ) : (
              <div className="w-full aspect-video bg-[var(--bg-hover)] rounded flex items-center justify-center text-xs text-[var(--text-muted)]">
                画像未設定
              </div>
            )}
            {it.caption && (
              <p className="text-xs text-center text-[var(--text-muted)] mt-2">
                {it.caption}
              </p>
            )}
          </div>
        ))}
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="前の画像"
              className="doc-no-print absolute left-2 top-1/2 -translate-y-1/2 size-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="次の画像"
              className="doc-no-print absolute right-2 top-1/2 -translate-y-1/2 size-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition"
            >
              <ChevronRight className="size-4" />
            </button>
          </>
        )}
      </div>
      {items.length > 1 && (
        <div className="doc-no-print flex justify-center gap-2 mt-3">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`画像 ${i + 1} を表示`}
              className={`size-2 rounded-full transition ${
                i === activeIndex
                  ? 'bg-[var(--text-primary)] scale-125'
                  : 'bg-[var(--border)] hover:bg-[var(--text-muted)]'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function InlineCardImage({ config }: RendererProps) {
  const v = config as unknown as InlineCardConfig;
  return (
    <section className="rounded-2xl shadow-lg overflow-hidden border border-[var(--border)] bg-[var(--bg-elevated)]">
      {v.src ? (
        <img src={v.src} alt={v.caption ?? ''} className="w-full" />
      ) : (
        <div className="w-full aspect-video bg-[var(--bg-hover)]" />
      )}
      {v.caption && (
        <div className="p-4 text-sm text-[var(--text-secondary)] text-center">
          {v.caption}
        </div>
      )}
    </section>
  );
}

function BeforeAfterEditor({ config, onChange, onCommit }: EditorProps) {
  const set = (patch: Partial<BeforeAfterConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <ImageField
        label="Before 画像"
        value={(config.beforeSrc as string) ?? ''}
        onChange={(v) => {
          set({ beforeSrc: v });
          onCommit();
        }}
      />
      <TextField
        label="Before ラベル"
        value={(config.beforeLabel as string) ?? 'Before'}
        onChange={(v) => set({ beforeLabel: v })}
        onCommit={onCommit}
      />
      <ImageField
        label="After 画像"
        value={(config.afterSrc as string) ?? ''}
        onChange={(v) => {
          set({ afterSrc: v });
          onCommit();
        }}
      />
      <TextField
        label="After ラベル"
        value={(config.afterLabel as string) ?? 'After'}
        onChange={(v) => set({ afterLabel: v })}
        onCommit={onCommit}
      />
      <TextField
        label="共通キャプション"
        value={(config.caption as string) ?? ''}
        onChange={(v) => set({ caption: v })}
        onCommit={onCommit}
      />
    </div>
  );
}

function BeforeAfterImage({ config }: RendererProps) {
  const v = config as unknown as BeforeAfterConfig;
  return (
    <section className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        {[
          { src: v.beforeSrc, label: v.beforeLabel ?? 'Before' },
          { src: v.afterSrc, label: v.afterLabel ?? 'After' },
        ].map((side, i) => (
          <div key={i} className="relative">
            {side.src ? (
              <img
                src={side.src}
                alt={side.label}
                className="w-full aspect-[4/3] object-cover rounded"
              />
            ) : (
              <div className="aspect-[4/3] bg-[var(--bg-hover)] rounded" />
            )}
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[11px] font-semibold bg-black/70 text-white">
              {side.label}
            </span>
          </div>
        ))}
      </div>
      {v.caption && (
        <p className="text-xs text-center text-[var(--text-muted)]">
          {v.caption}
        </p>
      )}
    </section>
  );
}

function SideBySideEditor({ config, onChange, onCommit }: EditorProps) {
  const v: SideBySideConfig = {
    src: (config.src as string) ?? '',
    imageSide: ((config.imageSide as SideBySideSide) ?? 'left') as SideBySideSide,
    eyebrow: (config.eyebrow as string) ?? '',
    title: (config.title as string) ?? '',
    body: (config.body as string) ?? '',
  };
  const set = (patch: Partial<SideBySideConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <ImageField
        label="画像"
        value={v.src}
        onChange={(x) => {
          set({ src: x });
          onCommit();
        }}
      />
      <SelectField<SideBySideSide>
        label="画像の位置"
        value={v.imageSide}
        onChange={(x) => set({ imageSide: x })}
        onCommit={onCommit}
        options={[
          { value: 'left', label: '左に画像' },
          { value: 'right', label: '右に画像' },
        ]}
      />
      <TextField
        label="アイブロウ (小さなラベル、任意)"
        value={v.eyebrow}
        onChange={(x) => set({ eyebrow: x })}
        onCommit={onCommit}
        placeholder="例: ABOUT DOCTOR"
      />
      <TextField
        label="見出し"
        value={v.title}
        onChange={(x) => set({ title: x })}
        onCommit={onCommit}
        placeholder="例: Konstantin Korotkov"
      />
      <TextareaField
        label="本文"
        value={v.body}
        onChange={(x) => set({ body: x })}
        onCommit={onCommit}
        rows={8}
        placeholder="プロフィール・実績などを記述..."
      />
    </div>
  );
}

function SideBySideImage({ config }: RendererProps) {
  const v: SideBySideConfig = {
    src: (config.src as string) ?? '',
    imageSide: ((config.imageSide as SideBySideSide) ?? 'left') as SideBySideSide,
    eyebrow: (config.eyebrow as string) ?? '',
    title: (config.title as string) ?? '',
    body: (config.body as string) ?? '',
  };
  const imageBlock = (
    // 印刷時はポートレート画像が肥大化しないよう幅を上限 260px、中央揃え
    <div className="w-full print:max-w-[260px] print:mx-auto">
      {v.src ? (
        <img
          src={v.src}
          alt={v.title}
          className="w-full aspect-[4/5] object-cover rounded"
        />
      ) : (
        <div className="w-full aspect-[4/5] bg-[var(--bg-hover)] rounded flex items-center justify-center text-xs text-[var(--text-muted)]">
          画像未設定
        </div>
      )}
    </div>
  );
  const textBlock = (
    <div className="flex flex-col justify-center">
      {v.eyebrow && (
        <p className="text-[11px] font-semibold tracking-widest uppercase text-[var(--text-muted)] mb-3">
          {v.eyebrow}
        </p>
      )}
      {v.title && (
        <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight print:text-2xl">
          {v.title}
        </h2>
      )}
      {v.body && (
        <div className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap print:text-[12px]">
          {v.body}
        </div>
      )}
    </div>
  );
  return (
    // 印刷時は 2 カラム維持・余白詰め・上揃え（画像は max-w で抑制）
    <section className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center py-10 print:grid-cols-2 print:gap-6 print:py-4 print:items-start">
      {v.imageSide === 'right' ? (
        <>
          {textBlock}
          {imageBlock}
        </>
      ) : (
        <>
          {imageBlock}
          {textBlock}
        </>
      )}
    </section>
  );
}

export const imageDefinition: SectionDefinition = {
  type: 'image',
  label: '画像',
  icon: ImageIcon,
  description: '1 枚画像 / ギャラリー / カルーセル / カード / Before-After / 左右並列。',
  defaultVariant: 'full',
  useCases: ['all'],
  variants: {
    full: {
      label: 'フル幅',
      description: '1 枚画像 + キャプション',
      Renderer: FullImage,
      Editor: FullEditor,
      sample: () => ({ src: '', caption: '' }),
    },
    gallery: {
      label: 'ギャラリー',
      description: '3-4 枚をグリッド表示',
      Renderer: GalleryImage,
      Editor: GalleryEditor,
      sample: () => ({
        items: [
          { src: '', caption: '画像 1' },
          { src: '', caption: '画像 2' },
          { src: '', caption: '画像 3' },
        ] as GalleryItem[],
      }),
    },
    carousel: {
      label: '画像ギャラリー (カルーセル)',
      description:
        '1 枚ずつ表示。下部のドット / 矢印で切替。PDF 印刷は設定で選んだ 1 枚のみ出力。',
      Renderer: CarouselImage,
      Editor: CarouselEditor,
      sample: () =>
        ({
          items: [
            { src: '', caption: '画像 1' },
            { src: '', caption: '画像 2' },
            { src: '', caption: '画像 3' },
          ] as CarouselItem[],
          printIndex: 0,
        }) as Record<string, unknown>,
    },
    inline_card: {
      label: 'カード',
      description: '角丸 + 影付きカードに収める',
      Renderer: InlineCardImage,
      Editor: FullEditor,
      sample: () => ({ src: '', caption: '' }),
    },
    before_after: {
      label: 'ビフォー / アフター',
      description: '2 枚並置、ラベル付き',
      Renderer: BeforeAfterImage,
      Editor: BeforeAfterEditor,
      sample: () =>
        ({
          beforeSrc: '',
          beforeLabel: 'Before',
          afterSrc: '',
          afterLabel: 'After',
          caption: '',
        }) as Record<string, unknown>,
    },
    side_by_side: {
      label: '左右並列 (画像+見出し+本文)',
      description: 'プロフィール・人物紹介向け。画像 + アイブロウ + 見出し + 本文の 50/50',
      Renderer: SideBySideImage,
      Editor: SideBySideEditor,
      sample: () =>
        ({
          src: '',
          imageSide: 'left',
          eyebrow: 'ABOUT',
          title: '見出しテキスト',
          body: 'プロフィールや製品の説明文をここに記述します。複数行・段落も入力できます。',
        }) as Record<string, unknown>,
    },
  },
};
